import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import type {
	HtmlString,
	TooltipFormatter,
	IssuesPerDeploymentTooltipFormatterParams,
} from '../../../../common/types';
import { upIcon, downIcon, issuesIcon } from './assets/icons';
import messages from './messages';

type IconInfo = {
	style: string;
	iconHtml: HtmlString;
};
const getIconInfo = (value: number, deploymentsComparisonTarget: number | null): IconInfo => {
	if (deploymentsComparisonTarget === null) {
		return { style: iconBase, iconHtml: '' };
	}
	if (value < deploymentsComparisonTarget) {
		return { style: iconWarn, iconHtml: downIcon };
	}
	if (value > deploymentsComparisonTarget) {
		return { style: iconGood, iconHtml: upIcon };
	}
	return { style: iconBase, iconHtml: '' };
};

export const useTooltipFormatter =
	(): TooltipFormatter<IssuesPerDeploymentTooltipFormatterParams> => {
		const { formatDate, formatMessage, formatNumber } = useIntl();
		return ({ data }: IssuesPerDeploymentTooltipFormatterParams) => {
			const {
				value,
				customInfo: { numProdDeployments, medianProdDeployments, startDateInclusive },
			} = data;

			const weekStartDateText = formatMessage(messages.weekStartDate, {
				date: formatDate(startDateInclusive, { day: 'numeric', month: 'short' }),
			});
			const statisticsText = formatMessage(messages.medianIssuesPerDeployment, {
				medianIssuesPerDeployment: formatNumber(value ?? 0),
			});
			const numProductionDeploymentsText = formatMessage(messages.numProductionDeployments, {
				numProdDeployments,
			});
			const { style: comparisonIconStyle, iconHtml } = getIconInfo(
				numProdDeployments,
				medianProdDeployments,
			);

			return `
            <div style="${container}">
                <div style="${header}">
                    <span style="${weekStart}">${weekStartDateText}</span>
                </div>
                <div>
                    <div style="${stack}">
                        <span style="${issuesIconStyled}">${issuesIcon}</span>
                        <span style="${statistics}">${statisticsText}</span>
                    </div>
                    <div style="${stack}">
                        <span style="${comparisonIconStyle}">${iconHtml}</span>
                        <span style="${statistics}">${numProductionDeploymentsText}</span>
                    </div>
                </div>
            </div>
        `;
		};
	};

const container = `
    height: ${gridSize * 9}px;
    padding: 0 ${gridSize / 2}px;
    background-color: ${token('color.background.neutral.bold', colors.N900)};
    border-radius: ${borderRadius};
    font-size: ${fontSizeSmall()}px;
    color: ${token('color.text.inverse', colors.N0)};
`;

const header = `
    display: flex;
    justify-content: space-between;
    height: ${gridSize * 2}px;
    margin-bottom: ${gridSize}px;
`;

const iconBase = `
    margin-top: -1px;
    height: 16px;
    width: 16px;
`;
const iconGood = `
    ${iconBase}
    fill: ${token('color.text', colors.N800)};
    color: ${token('color.icon.success', colors.G300)}
`;
const iconWarn = `
    ${iconBase}
    fill: ${token('elevation.surface', colors.N10)};
    color: ${token('color.icon.warning', colors.Y400)}
`;

const weekStart = `
    text-decoration-line: underline;
    margin-right: ${gridSize * 2.5}px;
`;

const issuesIconStyled = `
    ${iconBase}
`;

const statistics = `
  margin-left: ${gridSize / 2}px;
`;

const stack = `
    display: flex;
    align-items: center;
`;
